import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import WmsFullScreenLayout from '../shared/WmsFullScreenLayout';
import useWmsSession from '../shared/useWmsSession';
import WmsScreenAccessGate from '../shared/WmsScreenAccessGate';
import { screenService } from '../services/screenService';
import {
  waveService,
  parseRouteAlternatives,
  type RoutingStrategy,
  type RouteAlternative,
} from '../services/waveService';
import { orderService } from '../services/orderService';
import PickingHome from './picking/PickingHome';
import PickingTypeSelect, { PickingWaveType } from './picking/PickingTypeSelect';
import PickingSourceSelect from './picking/PickingSourceSelect';
import PickingPool, { type PoolWaveInfo } from './picking/PickingPool';
import PickingWaveSelect, { type PickingWaveSelectWave } from './picking/PickingWaveSelect';
import { PickingChannel } from './picking/pickingChannels';
import PickingCartScan, { ScannedCart, CartScanOrderRow } from './picking/PickingCartScan';
import PickingActiveFlow, { PickListItem } from './picking/PickingActiveFlow';
import PickingConsolidatedFlow from './picking/PickingConsolidatedFlow';
import PickingCompleted from './picking/PickingCompleted';
import PickingSortingFlow, { SlotAssignment } from './picking/PickingSortingFlow';
import SortingCompleted from './picking/SortingCompleted';
import type { CancelOrderResult } from './picking/CancelOrderDialog';

// Akis: kanal -> tekli/coklu -> (Siparis Havuzu | Is Emirleri) -> araba okut -> optimize toplama.
// Tip secilince kaynak secimine gecilir; devam eden dalga Is Emirleri uzerinden secilir.
type PickingPhase =
  | 'home'
  | 'type-select'
  | 'source-select'
  | 'pool'
  | 'wave-select'
  | 'cart-scan'
  | 'picking'
  | 'completed'
  | 'sorting'
  | 'sort-completed';

interface WaveInfo {
  id: string;
  waveNumber: string;
  waveType: string;
  status: string;
  totalOrders: number;
  totalItems: number;
  pickedItems: number;
  startedAt?: string;
}

const DEFAULT_PICKING_PERMISSIONS = {
  canPicking: true,
  canPacking: false,
  canReceiving: false,
  canCycleCount: false,
  pickingScreenId: null,
  packingScreenId: null,
  receivingScreenId: null,
  cycleCountScreenId: null,
};

const isCompletedPickStatus = (status?: string) =>
  status === 'Picked' || status === 'ShortPick' || status === 'Sorted';

const toWaveInfo = (w: any): WaveInfo => ({
  id: w.id,
  waveNumber: w.waveNumber,
  waveType: w.waveType,
  status: w.status,
  totalOrders: w.totalOrders,
  totalItems: w.totalItems,
  pickedItems: w.pickedItems,
  startedAt: w.startedAt,
});

const WmsPickingScreen: React.FC = () => {
  const { screenId } = useParams<{ screenId: string }>();
  const { t } = useTranslation('warehouse');
  const { session, logout } = useWmsSession();

  const [phase, setPhase] = useState<PickingPhase>('home');
  const [selectedChannel, setSelectedChannel] = useState<PickingChannel | null>(null);
  const [selectedType, setSelectedType] = useState<PickingWaveType | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [loadingScreen, setLoadingScreen] = useState(true);
  // Araba okutulup dalgaya baglanirken gosterilen yukleniyor durumu.
  const [claiming, setClaiming] = useState(false);
  // Araba okutma sayfasinda scanner altinda gosterilen siparis listesi.
  const [cartScanRows, setCartScanRows] = useState<CartScanOrderRow[]>([]);
  const [wave, setWave] = useState<WaveInfo | null>(null);
  const [pickList, setPickList] = useState<PickListItem[]>([]);
  const [cartBarcode, setCartBarcode] = useState('');
  const [pickedCount, setPickedCount] = useState(0);
  const [shortPickCount, setShortPickCount] = useState(0);
  const [sortedCount, setSortedCount] = useState(0);
  // Ayni kanal+tipte kalan bekleyen siparis adedi (tamamlama ekraninda "Sonraki grup" icin).
  const [remainingSameType, setRemainingSameType] = useState(0);
  const [sortSlotCount, setSortSlotCount] = useState(0);
  const [sortAssignments, setSortAssignments] = useState<SlotAssignment[]>([]);
  const [routeStrategy, setRouteStrategy] = useState<RoutingStrategy>('optimal');
  const [routeAlternatives, setRouteAlternatives] = useState<RouteAlternative[]>([]);
  // Konsolide endpoint (pick-list-consolidated) sunucuda yoksa Multi dalgayi da
  // kalem-kalem (PickingActiveFlow) akisina dusur.
  const [forceItemFlow, setForceItemFlow] = useState(false);

  useEffect(() => {
    if (!screenId) return;
    (async () => {
      try {
        const res = await screenService.getScreenDetail('picking', screenId);
        const data = res.data?.data || res.data;
        if (data?.warehouseId) setWarehouseId(data.warehouseId);
      } catch { /* screen not found */ }
      setLoadingScreen(false);
    })();
  }, [screenId]);

  const handleLogout = useCallback(() => {
    logout();
    setWave(null);
    setPickList([]);
    setCartBarcode('');
    setPhase('home');
  }, [logout]);

  // Sprint 2 operatör akışı: kanal → tekli/çoklu → hazır liste.
  const handleSelectChannel = useCallback((channel: PickingChannel) => {
    setSelectedChannel(channel);
    setSelectedType(null);
    setPhase('type-select');
  }, []);

  const mapPickListResponse = useCallback((data: any[]): PickListItem[] =>
    data.map((item: any) => ({
      orderItemId: item.orderItemId,
      orderId: item.orderId,
      orderNumber: item.orderNumber ?? item.OrderNumber,
      customerName: item.customerName ?? item.CustomerName,
      productId: item.productId,
      productName: item.productName,
      sku: item.sku || item.skU,
      barcode: item.barcode,
      quantity: item.quantity,
      pickedQuantity: item.pickedQuantity,
      locationId: item.locationId,
      locationCode: item.locationCode,
      locationName: item.locationName,
      sortOrder: item.sortOrder,
      status: item.status,
      aisleId: item.aisleId,
      aisleName: item.aisleName,
      aisleSide: item.aisleSide,
      gridX: item.gridX,
      gridY: item.gridY,
    })),
  []);

  const loadPickList = useCallback(async (waveId: string): Promise<PickListItem[]> => {
    const res = await waveService.getPickListOptimized(waveId, routeStrategy);
    const payload = res.data || {};
    setRouteAlternatives(parseRouteAlternatives(payload));
    const selected = (payload.strategy ?? payload.Strategy) as RoutingStrategy | undefined;
    if (selected && selected !== routeStrategy) {
      setRouteStrategy(selected);
    }
    return mapPickListResponse(payload.data || payload.Data || []);
  }, [mapPickListResponse, routeStrategy]);

  const reloadActivePickList = useCallback(async (): Promise<PickListItem[]> => {
    if (!wave?.id) return pickList;
    const list = await loadPickList(wave.id);
    setPickList(list);
    return list;
  }, [wave?.id, loadPickList, pickList]);

  // Pending havuz onizlemesi (kanal + tip filtreli).
  const loadPoolPreviewRows = useCallback(async (type: PickingWaveType): Promise<CartScanOrderRow[]> => {
    if (!selectedChannel || !warehouseId) return [];
    try {
      const cres = await orderService.getWaveCandidates(warehouseId, undefined, 200, selectedChannel.source);
      const orders: any[] = cres.data?.data ?? cres.data ?? [];
      return orders
        .filter((o) => {
          const ic = o.itemCount ?? o.ItemCount ?? 1;
          return type === 'Multi' ? ic > 1 : ic <= 1;
        })
        .map((o) => ({
          id: o.id ?? o.Id,
          primary: o.orderNumber ?? o.OrderNumber ?? o.customerName ?? o.CustomerName ?? '-',
          secondary: o.customerName ?? o.CustomerName ?? undefined,
          done: false,
          statusLabel: t('screens.picking.single.pending'),
        }));
    } catch {
      return [];
    }
  }, [selectedChannel, warehouseId, t]);

  // Devam eden / secilen dalgadaki kalemleri araba ekrani onizlemesi icin satir yap.
  const loadWavePreviewRows = useCallback(async (waveId: string): Promise<CartScanOrderRow[]> => {
    try {
      const list = await loadPickList(waveId);
      const byOrder = new Map<string, CartScanOrderRow>();
      for (const item of list) {
        const key = item.orderId || item.orderNumber || item.orderItemId;
        if (byOrder.has(key)) continue;
        byOrder.set(key, {
          id: key,
          primary: item.orderNumber || item.customerName || '-',
          secondary: item.customerName || undefined,
          done: isCompletedPickStatus(item.status),
          statusLabel: t('screens.picking.single.pending'),
        });
      }
      return Array.from(byOrder.values());
    } catch {
      return [];
    }
  }, [loadPickList, t]);

  // InProgress dalgada ekrandan cikis-donus: kaldigi fazdan devam et.
  const resumeInProgressWave = useCallback(async (w: WaveInfo) => {
    setWave(w);
    setForceItemFlow(false);

    if (w.status !== 'InProgress') {
      setPhase('source-select');
      return;
    }

    // Henuz toplama baslamamis / araba yoksa once araba okut.
    if ((w.pickedItems || 0) === 0 && !w.startedAt) {
      const rows = await loadWavePreviewRows(w.id);
      setCartScanRows(rows);
      setPhase('cart-scan');
      return;
    }

    try {
      const list = await loadPickList(w.id);
      setPickList(list);

      const hasPendingPick = list.some((item) => !isCompletedPickStatus(item.status));
      if (hasPendingPick) {
        // Araba henuz yoksa ve hicbir kalem toplanmamissa araba adimina dus.
        const anyPicked = list.some((item) => isCompletedPickStatus(item.status));
        if (!anyPicked) {
          const rows = await loadWavePreviewRows(w.id);
          setCartScanRows(rows);
          setPhase('cart-scan');
          return;
        }
        setPhase('picking');
        return;
      }

      if (w.waveType === 'Multi') {
        const sortable = list.filter((item) => item.status === 'Picked' || item.status === 'Sorted');
        const alreadySorted = sortable.filter((item) => item.status === 'Sorted').length;
        if (sortable.length > 0 && alreadySorted < sortable.length) {
          setPhase('sorting');
          return;
        }
      }

      const truePicked = list.filter((i) => i.status === 'Picked' || i.status === 'Sorted').length;
      const trueShort = list.filter((i) => i.status === 'ShortPick').length;
      setPickedCount(truePicked);
      setShortPickCount(trueShort);
      try {
        await waveService.completePicking(w.id);
      } catch {
        // Dalga zaten tamamlanmis olabilir (idempotent); sessizce devam et.
      }
      setPhase('completed');
    } catch {
      setPhase('source-select');
    }
  }, [loadPickList, loadWavePreviewRows]);

  // Is emri listesinden secilen (baslatilan) dalga: araba okutmaya gec.
  const handleExistingWaveSelected = useCallback(async (selected: PickingWaveSelectWave) => {
    const w = toWaveInfo(selected);
    setWave(w);
    setForceItemFlow(false);

    // Gercekten devam eden (kalem toplanmis) dalga → faz resume.
    if (selected.status === 'InProgress' && (selected.pickedItems || 0) > 0) {
      await resumeInProgressWave(w);
      return;
    }

    const rows = await loadWavePreviewRows(w.id);
    setCartScanRows(rows);
    setPhase('cart-scan');
  }, [resumeInProgressWave, loadWavePreviewRows]);

  // Siparis havuzundan olusturulan is emri → araba okutmaya gec.
  const handlePoolWaveStarted = useCallback(async (w: PoolWaveInfo) => {
    const waveInfo = toWaveInfo(w);
    setWave(waveInfo);
    setForceItemFlow(false);
    const rows = await loadWavePreviewRows(waveInfo.id);
    setCartScanRows(rows);
    setPhase('cart-scan');
  }, [loadWavePreviewRows]);

  // Tekli/coklu secilince Siparis Havuzu / Is Emirleri ekranina gec.
  // Yarim kalan dalga otomatik resume edilmez; kullanici Is Emirleri'nden secer.
  const handleSelectType = useCallback((type: PickingWaveType) => {
    if (!selectedChannel || !warehouseId || claiming) return;
    setSelectedType(type);
    setPickList([]);
    setCartBarcode('');
    setCartScanRows([]);
    setWave(null);
    setForceItemFlow(false);
    setPhase('source-select');
  }, [selectedChannel, warehouseId, claiming]);

  // Ayni kanal+tipte henuz ATANMAMIS (Pending) siparis adedi — "Sonraki grup" icin.
  const fetchRemaining = useCallback(async (type: PickingWaveType): Promise<number> => {
    const rows = await loadPoolPreviewRows(type);
    return rows.length;
  }, [loadPoolPreviewRows]);

  // Tamamlama ekranindan "Sonraki grup": ayni kanal+tipte kaynak secimine don.
  const handleContinueSameType = useCallback(() => {
    const type = selectedType;
    setWave(null);
    setPickList([]);
    setCartBarcode('');
    setPickedCount(0);
    setShortPickCount(0);
    setSortedCount(0);
    setSortSlotCount(0);
    setSortAssignments([]);
    setForceItemFlow(false);
    setRemainingSameType(0);
    if (type) handleSelectType(type);
    else setPhase('type-select');
  }, [selectedType, handleSelectType]);

  // Toplama listesini yukler; basarisiz olursa (or. DB timeout) phase'i ilerletmeden
  // hata firlatir. Boylece "TOPLAMA TAMAMLANDI"/"Tum urunler toplandi" yaniltmasi olmaz.
  const enterPickingWithList = useCallback(async (w: WaveInfo) => {
    const list = await loadPickList(w.id);
    setPickList(list);
    setPhase('picking');
  }, [loadPickList]);

  // Araba okutuldu (veya sepetsiz devam): is emri zaten havuz/listeden olusturulmus olmali.
  // Arabayi dalgaya bagla ve toplamaya gec.
  const handleClaimWithCart = useCallback(async (cart: ScannedCart | null) => {
    if (!warehouseId || claiming || !wave) return;
    setClaiming(true);
    try {
      if (cart?.id) {
        try { await waveService.assignCart({ WaveId: wave.id, CartId: cart.id }); } catch { /* araba atama opsiyonel */ }
      }
      setCartBarcode(cart?.cartCode || '');
      await enterPickingWithList(wave);
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.Message
        || err?.message
        || t('screens.picking.screen.unknownError');
      // eslint-disable-next-line no-alert
      alert(t('screens.picking.waveSelect.startFailedAlert', { msg }));
    } finally {
      setClaiming(false);
    }
  }, [warehouseId, claiming, wave, enterPickingWithList, t]);

  const handlePickingComplete = useCallback(async (picked: number, shorts: number) => {
    if (!wave) {
      setPickedCount(picked);
      setShortPickCount(shorts);
      setPhase('completed');
      return;
    }

    // Frontend'den gelen sayilar guvenilmez olabiliyor (state senkronizasyonu, eski data,
    // Atla butonu vb.). Bu yuzden tamamlandi/sorting kararini backend'in son durumuna
    // gore verelim — pickList'i tazele, gercek statuleri say.
    let refreshedList: PickListItem[] = [];
    try {
      refreshedList = await loadPickList(wave.id);
      setPickList(refreshedList);
    } catch {
      // Refresh yapilamadi: provided sayilarla devam et.
    }

    const truePicked = refreshedList.length > 0
      ? refreshedList.filter((i) => i.status === 'Picked' || i.status === 'Sorted').length
      : picked;
    const trueShort = refreshedList.length > 0
      ? refreshedList.filter((i) => i.status === 'ShortPick').length
      : shorts;

    setPickedCount(truePicked);
    setShortPickCount(trueShort);

    // Cok-siparisli (Multi) dalga normalde ayristirmaya gider, AMA basariyla toplanmis
    // (status='Picked') urun yoksa ayristirilacak hicbir sey olmadigi icin direkt tamamlandi
    // ekranina goturuyoruz. Aksi halde sorting ekrani bos acilir ("0/0 urun") ve takilir.
    const needsSorting = wave.waveType === 'Multi' && truePicked > 0;

    if (needsSorting) {
      setPhase('sorting');
      return;
    }

    // Sorting'e gerek yok: backend'de dalgayi Completed isaretle. Hata olursa kullaniciya
    // bildir; sessiz yutmak "TOPLAMA TAMAMLANDI" gosterirken dalganin listede kalmasina
    // sebep olur (gecmis bug).
    try {
      await waveService.completePicking(wave.id);
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.Message
        || err?.message
        || t('screens.picking.screen.completeFailed');
      // 400: backend guard — hâlâ çözülmemiş (toplanmamış / stok girilmemiş / short-pick edilmemiş)
      // kalem var. Bu durumda "TAMAMLANDI" göstermek yanıltıcı olur; toplamaya geri dön.
      if (err?.response?.status === 400) {
        console.warn('[WMS PICKING] completePicking blocked by guard:', msg);
        // eslint-disable-next-line no-alert
        alert(t('screens.picking.screen.completeFailedAlert', { msg }));
        setPhase('picking');
        return;
      }
      console.error('[WMS PICKING] completePicking error:', err);
      // eslint-disable-next-line no-alert
      alert(t('screens.picking.screen.completeErrorAlert', { msg }));
    }
    // Tekli: tum tekliler tek dalgada toplandi -> "Sonraki grup" yok (tamamlandi EN SON).
    // Coklu: kalan bekleyen grup varsa "Sonraki grup" gosterilir.
    if (wave.waveType === 'Multi') {
      try { setRemainingSameType(await fetchRemaining('Multi')); } catch { /* ignore */ }
    } else {
      setRemainingSameType(0);
    }
    setPhase('completed');
  }, [wave, loadPickList, fetchRemaining, t]);

  const handleSortingComplete = useCallback(async (sorted: number, slotCount: number, assignments: SlotAssignment[]) => {
    setSortedCount(sorted);
    setSortSlotCount(slotCount);
    setSortAssignments(assignments);
    if (wave) {
      try { await waveService.completeSorting(wave.id); } catch { /* ignore */ }
    }
    try { setRemainingSameType(await fetchRemaining('Multi')); } catch { /* ignore */ }
    setPhase('sort-completed');
  }, [wave, fetchRemaining]);

  const handleNewWave = useCallback(() => {
    setWave(null);
    setPickList([]);
    setCartScanRows([]);
    setCartBarcode('');
    setPickedCount(0);
    setShortPickCount(0);
    setSortedCount(0);
    setSortSlotCount(0);
    setSortAssignments([]);
    setRemainingSameType(0);
    setRouteStrategy('optimal');
    setForceItemFlow(false);
    setSelectedChannel(null);
    setSelectedType(null);
    setPhase('home');
  }, []);

  const handleOrderCancelled = useCallback((result: CancelOrderResult) => {
    // Multi'de kalan kalem varsa picking ekrani listeden devam eder (bir sey yapma).
    // Dalga bittiyse ana menuye degil ayni kanal+tipte sonraki siparise devam et
    // (araba okut / claim) — tamamlama ekranindaki "Sonraki grup" ile ayni mantik.
    if (!result.waveStillActive || result.remainingPendingItems <= 0) {
      handleContinueSameType();
    }
  }, [handleContinueSameType]);

  useEffect(() => {
    if (phase !== 'picking' || !wave?.id) return;
    loadPickList(wave.id)
      .then(setPickList)
      .catch(() => {});
  }, [phase, wave?.id, routeStrategy, loadPickList]);

  const content = loadingScreen ? (
    <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fb' }}>
      <CircularProgress />
    </Box>
  ) : (
    <WmsFullScreenLayout
      currentScreen="picking"
      cashierName={session?.cashierName || ''}
      wmsPermissions={session?.wmsPermissions || DEFAULT_PICKING_PERMISSIONS}
      onLogout={handleLogout}
    >
      {phase === 'home' && (
        <PickingHome
          personnelName={session?.personnelName || session?.cashierName || ''}
          warehouseId={warehouseId}
          onSelectChannel={handleSelectChannel}
        />
      )}

      {phase === 'type-select' && selectedChannel && (
        <PickingTypeSelect
          warehouseId={warehouseId}
          channel={selectedChannel}
          onSelectType={handleSelectType}
          onBack={() => setPhase('home')}
        />
      )}

      {phase === 'source-select' && selectedChannel && selectedType && (
        <PickingSourceSelect
          personnelName={session?.personnelName || session?.cashierName || ''}
          personnelId={session?.personnelId || session?.cashierName || undefined}
          warehouseId={warehouseId}
          channel={selectedChannel}
          waveType={selectedType}
          onSelectPool={() => setPhase('pool')}
          onSelectWorkOrders={() => setPhase('wave-select')}
          onBack={() => setPhase('type-select')}
        />
      )}

      {phase === 'pool' && selectedChannel && selectedType && (
        <PickingPool
          personnelName={session?.personnelName || session?.cashierName || ''}
          personnelId={session?.personnelId || session?.cashierName || undefined}
          warehouseId={warehouseId}
          orderType={selectedType}
          channel={selectedChannel.source}
          channelLabel={selectedChannel.label}
          onBack={() => setPhase('source-select')}
          onWaveStarted={handlePoolWaveStarted}
        />
      )}

      {phase === 'wave-select' && selectedChannel && selectedType && (
        <PickingWaveSelect
          personnelName={session?.personnelName || session?.cashierName || ''}
          personnelId={session?.personnelId || session?.cashierName || undefined}
          warehouseId={warehouseId}
          channel={selectedChannel.source}
          channelLabel={selectedChannel.label}
          waveType={selectedType}
          onWaveSelected={handleExistingWaveSelected}
          onBack={() => setPhase('source-select')}
        />
      )}

      {phase === 'cart-scan' && selectedChannel && selectedType && (
        <PickingCartScan
          warehouseId={warehouseId}
          channel={selectedChannel}
          waveType={selectedType}
          items={cartScanRows}
          claiming={claiming}
          onClaim={handleClaimWithCart}
          onCancel={() => {
            setWave(null);
            setPhase('source-select');
          }}
        />
      )}

      {phase === 'picking' && wave && wave.waveType === 'Single' && (
        <PickingActiveFlow
          key={`single-${wave.id}-${routeStrategy}`}
          waveId={wave.id}
          waveNumber={wave.waveNumber}
          cartBarcode={cartBarcode}
          pickList={pickList}
          warehouseId={warehouseId}
          routeStrategy={routeStrategy}
          onRouteStrategyChange={setRouteStrategy}
          routeAlternatives={routeAlternatives}
          onComplete={handlePickingComplete}
          onPickListUpdate={setPickList}
          onReloadPickList={reloadActivePickList}
          onBack={() => setPhase('source-select')}
          backLabel={t('screens.picking.sourceSelect.back')}
          cancelledById={session?.personnelId || String(session?.cashierId || '')}
          cancelledByName={session?.personnelName || session?.cashierName || ''}
          onOrderCancelled={handleOrderCancelled}
        />
      )}

      {phase === 'picking' && wave && wave.waveType === 'Multi' && !forceItemFlow && (
        <PickingConsolidatedFlow
          key={`consolidated-${wave.id}-${routeStrategy}`}
          waveId={wave.id}
          waveNumber={wave.waveNumber}
          cartBarcode={cartBarcode}
          warehouseId={warehouseId}
          routeStrategy={routeStrategy}
          onRouteStrategyChange={setRouteStrategy}
          onComplete={handlePickingComplete}
          onConsolidatedUnavailable={() => setForceItemFlow(true)}
          cancelledById={session?.personnelId || String(session?.cashierId || '')}
          cancelledByName={session?.personnelName || session?.cashierName || ''}
          onOrderCancelled={handleOrderCancelled}
        />
      )}

      {phase === 'picking' && wave && wave.waveType === 'Multi' && forceItemFlow && (
        <PickingActiveFlow
          key={`${wave.id}-${routeStrategy}`}
          waveId={wave.id}
          waveNumber={wave.waveNumber}
          cartBarcode={cartBarcode}
          pickList={pickList}
          warehouseId={warehouseId}
          routeStrategy={routeStrategy}
          onRouteStrategyChange={setRouteStrategy}
          routeAlternatives={routeAlternatives}
          onComplete={handlePickingComplete}
          onPickListUpdate={setPickList}
          onReloadPickList={reloadActivePickList}
          onBack={() => setPhase('source-select')}
          cancelledById={session?.personnelId || String(session?.cashierId || '')}
          cancelledByName={session?.personnelName || session?.cashierName || ''}
          onOrderCancelled={handleOrderCancelled}
        />
      )}

      {phase === 'sorting' && wave && (
        <PickingSortingFlow
          waveId={wave.id}
          waveNumber={wave.waveNumber}
          cartBarcode={cartBarcode}
          warehouseId={warehouseId}
          pickList={pickList}
          onComplete={handleSortingComplete}
        />
      )}

      {phase === 'sort-completed' && wave && (
        <SortingCompleted
          waveNumber={wave.waveNumber}
          sortedCount={sortedCount}
          totalItems={pickList.filter(i => i.status === 'Picked' || i.status === 'Sorted').length}
          slotCount={sortSlotCount}
          assignments={sortAssignments}
          startedAt={wave.startedAt}
          remainingCount={remainingSameType}
          onContinue={handleContinueSameType}
          onNewWave={handleNewWave}
          onLogout={handleLogout}
        />
      )}

      {phase === 'completed' && wave && (
        <PickingCompleted
          waveNumber={wave.waveNumber}
          waveType={wave.waveType}
          totalItems={wave.totalItems}
          pickedCount={pickedCount}
          shortPickCount={shortPickCount}
          startedAt={wave.startedAt}
          remainingCount={remainingSameType}
          onContinue={handleContinueSameType}
          onNewWave={handleNewWave}
          onLogout={handleLogout}
        />
      )}
    </WmsFullScreenLayout>
  );

  return (
    <WmsScreenAccessGate screenType="picking" screenId={screenId || ''}>
      {content}
    </WmsScreenAccessGate>
  );
};

export default WmsPickingScreen;
